import type { ISODate, RecordDayType } from "../types.js";
import type { MarketCalendar } from "./business-days.js";
import { daysInMonth, toISODate } from "./date.js";

export interface RecordDateEvent {
  /** 権利確定日 */
  recordDate: ISODate;
  /** 権利付最終日 = 権利確定日の2営業日前 */
  lastTradingDay: ISODate;
  /** 権利落ち日 = 権利確定日の1営業日前 */
  exDate: ISODate;
}

/** Resolve the calendar 権利確定日 for a given year/month and day type. */
export function recordDateFor(
  y: number,
  m: number,
  dayType: RecordDayType,
  day?: number,
): ISODate {
  switch (dayType) {
    case "month_end":
      return toISODate(y, m, daysInMonth(y, m));
    case "day_15":
      return toISODate(y, m, 15);
    case "day_20":
      return toISODate(y, m, 20);
    case "other":
      if (day === undefined) throw new Error("recordDay is required for dayType 'other'");
      return toISODate(y, m, Math.min(day, daysInMonth(y, m)));
  }
}

/**
 * Derive 権利付最終日 and 権利落ち日 from a 権利確定日.
 * When the record date is not a business day, the last business day on or
 * before it is used as the base (this matches how exchanges treat month-end
 * record dates falling on weekends).
 */
export function recordDateEvent(cal: MarketCalendar, recordDate: ISODate): RecordDateEvent {
  const base = cal.prevBusinessDay(recordDate);
  return {
    recordDate,
    lastTradingDay: cal.addBusinessDays(base, -2),
    exDate: cal.addBusinessDays(base, -1),
  };
}

/** All record-date events for one stock over a year. */
export function recordDateEventsForYear(
  cal: MarketCalendar,
  y: number,
  recordMonths: number[],
  dayType: RecordDayType,
  day?: number,
): RecordDateEvent[] {
  return recordMonths
    .slice()
    .sort((a, b) => a - b)
    .map((m) => recordDateEvent(cal, recordDateFor(y, m, dayType, day)));
}
